import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCampaign,
  createCoupons,
  getCampaign,
  getCampaignStats,
  listCampaigns,
  listCoupons,
  listIssuedCoupons,
  listRedemptions,
  patchCampaign,
  patchCoupon,
  replaceCampaignTargets,
  setCampaignStatus,
  type CampaignListParams,
} from '../api/adminCampaigns.api';
import type { CampaignInput, CouponCreateInput, TargetInput } from '../types';

const keys = {
  all: ['admin', 'campaigns'] as const,
  list: (params: CampaignListParams) => [...keys.all, 'list', params] as const,
  detail: (id?: string) => [...keys.all, 'detail', id] as const,
  coupons: (id: string, page: number, size: number) =>
    [...keys.all, 'coupons', id, page, size] as const,
  redemptions: (id: string, page: number, size: number) =>
    [...keys.all, 'redemptions', id, page, size] as const,
  issued: (id: string, page: number, size: number) =>
    [...keys.all, 'issued', id, page, size] as const,
  stats: (id: string) => [...keys.all, 'stats', id] as const,
};

export const useCampaignList = (params: CampaignListParams) =>
  useQuery({
    queryKey: keys.list(params),
    queryFn: () => listCampaigns(params),
    staleTime: 10_000,
  });

export const useCampaign = (publicId?: string) =>
  useQuery({
    queryKey: keys.detail(publicId),
    queryFn: () => getCampaign(publicId as string),
    enabled: !!publicId,
  });

export const useCampaignStats = (publicId: string) =>
  useQuery({ queryKey: keys.stats(publicId), queryFn: () => getCampaignStats(publicId) });

export const useCoupons = (publicId: string, page: number, size: number) =>
  useQuery({
    queryKey: keys.coupons(publicId, page, size),
    queryFn: () => listCoupons(publicId, page, size),
  });

export const useIssuedCoupons = (publicId: string, page: number, size: number) =>
  useQuery({
    queryKey: keys.issued(publicId, page, size),
    queryFn: () => listIssuedCoupons(publicId, page, size),
  });

export const useRedemptions = (publicId: string, page: number, size: number) =>
  useQuery({
    queryKey: keys.redemptions(publicId, page, size),
    queryFn: () => listRedemptions(publicId, page, size),
  });

function useInvalidating<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  });
}

export const useCreateCampaign = () =>
  useInvalidating((input: CampaignInput) => createCampaign(input));
export const usePatchCampaign = () =>
  useInvalidating((a: { publicId: string; input: Partial<CampaignInput> }) =>
    patchCampaign(a.publicId, a.input)
  );
export const useSetCampaignStatus = () =>
  useInvalidating((a: { publicId: string; action: 'activate' | 'pause' }) =>
    setCampaignStatus(a.publicId, a.action)
  );
export const useReplaceTargets = () =>
  useInvalidating((a: { publicId: string; targets: TargetInput[] }) =>
    replaceCampaignTargets(a.publicId, a.targets)
  );
export const useCreateCoupons = () =>
  useInvalidating((a: { publicId: string; input: CouponCreateInput }) =>
    createCoupons(a.publicId, a.input)
  );
export const usePatchCoupon = () =>
  useInvalidating(
    (a: { publicId: string; input: { isActive?: boolean; maxUsage?: number | null } }) =>
      patchCoupon(a.publicId, a.input)
  );
